import { Box, Button, IconButton, MenuItem } from "@mui/material";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import { TrackDetailsContainerForAccordion } from "../../pages/Tracks/styles";
import { CustomContributorsContainer } from "../../pages/Tracks/styles";
import { CustomTrackListTitle, DateContainer } from "../../pages/Tracks/styles";
import { styles, CustomActionsContainer } from "../../pages/Tracks/styles";
import { CustomVersionsCountContainer } from "../../pages/Tracks/styles";
import { Collaborators } from "./Collaborators";
import { THEME } from "../../theme";
import { AccordionDetailsProps } from "./types";
import { GetTrackVersionsResponse } from "../../proto/api_pb";
import { KeyNote, TrackVersion } from "../../proto/api_pb";
import { TrackLeftSideContainer, TrackNameAndStatusContainer } from "./styles";
import { TrackNameAndCheckBoxContainer } from "./styles";
import { CollAndVersionContainer } from "./styles";
import { CustomIconButtonForPlayPause } from "./styles";
import { TrackVersionsObj } from "../../pages/MyTracks/types";
import { useContext, useEffect, useState } from "react";
import { useCallback } from "react";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import {
  deleteTrackVersion,
  postTrack,
  postTrackVersion,
} from "../../services/trackService";
import { fetchTrackVersions } from "../../services/trackService";
import { RpcError } from "grpc-web";
import { secondsToMinutes } from "./helper";
import { StatusAndNote } from "./StatusAndNote";
import { toast } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";
import { shareModalStyles } from "../Modal/styles";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { DeleteVersionModal } from "../Modal/DeleteVersionModal";

export const AccordionDetails: React.FC<AccordionDetailsProps> = ({
  track,
  handlePlayButtonClick,
  setVersionToPlay,
  setIsTrackForPlay,
  setAudioDetails,
  setLatestVersion,
  handleOpenTrackVersionModal,
  handleRefreshTracks,
  expanded,
  versionToPlay,
  setTrackToPlay,
  handleApplyFilterAndSort,
  shareToken,
}) => {
  const [trackVersions, setTrackVersions] = useState<TrackVersionsObj[]>([]);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [latestVersionNumber, setLatestVersionNumber] = useState(0);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDeleteInProgress, setIsDeleteInProgress] = useState<boolean>(false);
  const isMediumOrLarger = useMediaQuery(theme.breakpoints.up("md"));
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const [isDeleteVersionModalOpen, setIsDeleteVersionModalOpen] =
    useState(false);
  const [selectedVersion, setSelectedVersion] = useState<TrackVersion>();
  const { isEnterpriseAccount, enterprise } = useContext(UserDetailsContext);
  const isSharedContentPage = window.location.pathname.includes("/share/");

  const handleFetchTrackVersions = useCallback(async () => {
    if (expanded) {
      setIsLoading(true);
      const token = await getValidAccessToken();
      try {
        const trackId = track.track?.getId();
        const response = (await fetchTrackVersions(
          isSharedContentPage ? shareToken! : token!,
          trackId!
        )) as GetTrackVersionsResponse;

        const newTrackVersionList = response
          .getTrackVersionsList()
          .map((el) => {
            return {
              track: el,
              isPlaying: false,
              isSelected: track.track?.getTrackVersionId() === el.getId(),
            };
          });
        newTrackVersionList.forEach((v) => {
          if (v.track.getIsPrimary()) {
            setLatestVersion(v.track);
          }
        });
        newTrackVersionList.sort(
          (a, b) => b.track.getVersionNumber() - a.track.getVersionNumber()
        );
        setLatestVersionNumber(newTrackVersionList[0].track.getVersionNumber());
        setTrackVersions(
          newTrackVersionList.filter(
            (version) => version.track.getIsPrimary() !== true
          )
        );
      } catch (e) {
        const error = e as RpcError;
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    expanded,
    getValidAccessToken,
    isSharedContentPage,
    setLatestVersion,
    shareToken,
    track.track,
  ]);

  useEffect(() => {
    handleFetchTrackVersions();
  }, [handleFetchTrackVersions]);

  const handleOpenDeleteVersionModal = (version: TrackVersion) => {
    setSelectedVersion(version);
    setIsDeleteVersionModalOpen(true);
  };

  const handleDeleteVersion = async (versionId: TrackVersion | undefined) => {
    if (versionId) {
      try {
        setTrackVersions([]);
        setIsLoading(true);
        const token = await getValidAccessToken();
        await deleteTrackVersion(
          versionId.getId(),
          token,
          isEnterpriseAccount ? enterprise?.id : undefined
        );
      } catch (error) {
        console.log(error);
      } finally {
        setIsDeleteInProgress(false);
        setIsDeleteVersionModalOpen(false);
        setIsLoading(false);
        handleFetchTrackVersions();
      }
    }
  };

  const makePrimaryVersion = async (version: TrackVersion) => {
    try {
      setTrackVersions([]);
      setIsLoading(true);
      setIsDeleteInProgress(true);
      const token = await getValidAccessToken();
      const primaryVersion = version;
      primaryVersion.setIsPrimary(true);
      await postTrackVersion(
        primaryVersion,
        token,
        userId,
        isEnterpriseAccount ? enterprise?.id : undefined
      );
      if (track.track) {
        track.track.setTrackVersionId(primaryVersion.getId());
        await postTrack(track.track, await getValidAccessToken(), userId);
      }
      handleFetchTrackVersions();
      toast.info("Version set as primary");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      {isDeleteVersionModalOpen && (
        <DeleteVersionModal
          isModalOpen={isDeleteVersionModalOpen}
          handleCloseModal={() => setIsDeleteVersionModalOpen(false)}
          selectedVersion={selectedVersion}
          handleDeleteVersion={handleDeleteVersion}
          isLoading={isDeleteInProgress}
        />
      )}
      {isLoading && (
        <Box sx={styles.isLoadingContainer}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && trackVersions.length === 0 && (
        <Box sx={styles.isLoadingContainer}>
          <Typography>No version found!</Typography>
        </Box>
      )}
      <Box sx={styles.versionScrollContainer}>
        {trackVersions.map((version, i) => {
          const timestamp = version.track?.getTimestamp() ?? 0;
          const date = new Date(timestamp * 1000);
          const year = date.getFullYear();
          const mm = date.getMonth() + 1;
          const dd = date.getDate();
          return (
            <Box key={i} sx={{ backgroundColor: "#1F242F" }}>
              <TrackDetailsContainerForAccordion
                sx={{ backgroundColor: "#1F242F" }}
              >
                <TrackLeftSideContainer>
                  <TrackNameAndStatusContainer>
                    <TrackNameAndCheckBoxContainer>
                      <TrackNameAndCheckBoxContainer
                        sx={{ marginLeft: isSharedContentPage ? -6 : -1 }}
                      >
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                            marginLeft: isMediumOrLarger ? "47px" : 0,
                          }}
                        >
                          <img
                            style={{
                              ...styles.image,
                              opacity: 0.6,
                              marginLeft: isSmallScreen ? "10px" : 0,
                            }}
                            src={
                              track.track?.getImageUrl() &&
                              track.track?.getImageUrl().includes("http")
                                ? track.track?.getImageUrl()
                                : isEnterpriseAccount
                                ? enterprise?.image
                                : "/assets/images/logo512.png"
                            }
                            alt=""
                          />
                          <CustomIconButtonForPlayPause
                            sx={{
                              marginLeft: isSmallScreen ? "10px" : 0,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTrackToPlay(undefined);
                              handlePlayButtonClick();
                              setIsTrackForPlay(false);
                              setVersionToPlay((prev) => {
                                const isPlaying = prev?.isPlaying
                                  ? !prev.isPlaying
                                  : true;
                                return {
                                  track: version.track,
                                  isPlaying:
                                    versionToPlay?.track?.getId() ===
                                    version.track?.getId()
                                      ? isPlaying
                                      : true,
                                  isSelected:
                                    versionToPlay?.track?.getId() ===
                                    version.track?.getId()
                                      ? isPlaying
                                      : true,
                                };
                              });

                              if (track.track) {
                                setAudioDetails({
                                  title: track.track?.getTitle(),
                                  image: track.track?.getImageUrl(),
                                });
                              }
                            }}
                          >
                            {versionToPlay &&
                            versionToPlay.isPlaying &&
                            versionToPlay.track?.getId() ===
                              version.track?.getId() ? (
                              <img
                                style={{
                                  ...styles.pauseIcon,
                                  filter:
                                    THEME === "light"
                                      ? "invert(0%)"
                                      : "invert(100%)",
                                }}
                                src="/assets/icons/TracksPageIcons/circle_pause_icon.svg"
                                alt="icon"
                              />
                            ) : (
                              <img
                                style={{
                                  filter: "brightness(0) invert(1)",
                                  color: "white",
                                }}
                                src="/assets/icons/TracksPageIcons/play.svg"
                                alt="icon"
                              />
                            )}
                          </CustomIconButtonForPlayPause>
                        </div>
                        <Box sx={{ marginTop: "20px" }}>
                          <CustomTrackListTitle sx={{ marginLeft: "10px" }}>
                            {track.track?.getTitle()}
                          </CustomTrackListTitle>
                          <p style={styles.audioMetaData}>
                            {version.track &&
                              secondsToMinutes(
                                version.track?.getLengthSeconds()
                              )}{" "}
                            {version.track?.getAudioMetadata()?.getBpm()} BPM{" "}
                            {`${version.track
                              ?.getAudioMetadata()
                              ?.getKey()
                              ?.getKey()}${
                              version.track
                                ?.getAudioMetadata()

                                ?.getKey()
                                ?.getNote() === KeyNote.KEY_NOTE_FLAT
                                ? "♭"
                                : version.track
                                    ?.getAudioMetadata()

                                    ?.getKey()
                                    ?.getNote() === KeyNote.KEY_NOTE_SHARP
                                ? "♯"
                                : ""
                            }` || ""}
                          </p>
                        </Box>
                      </TrackNameAndCheckBoxContainer>
                    </TrackNameAndCheckBoxContainer>
                    {!isSharedContentPage && (
                      <Box
                        sx={{
                          marginLeft: isSmallScreen ? 0 : "15px",
                          marginBottom: isSmallScreen ? -1.5 : 0,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={styles.statusNoteContainer}>
                          {version.track && (
                            <StatusAndNote
                              version={version.track}
                              handleRefreshTracks={handleRefreshTracks}
                              handleFetchTrackVersions={
                                handleFetchTrackVersions
                              }
                              track={track.track!}
                              handleApplyFilterAndSort={
                                handleApplyFilterAndSort
                              }
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                  </TrackNameAndStatusContainer>
                  {!isSharedContentPage && (
                    <DateContainer>
                      <Typography
                        sx={{ marginLeft: 2 }}
                      >{`${mm}/${dd}/${year}`}</Typography>
                    </DateContainer>
                  )}
                  {!isSharedContentPage && (
                    <CollAndVersionContainer>
                      <CustomContributorsContainer>
                        {track.track && (
                          <Collaborators
                            collaborators={
                              track.track.getCollaboratorsList() ?? []
                            }
                            isGridView={false}
                          />
                        )}
                      </CustomContributorsContainer>
                      <CustomVersionsCountContainer>
                        v{" "}
                        {version.track?.getVersionNumber()
                          ? /^\d$/.test(`${version.track.getVersionNumber()}`)
                            ? version.track.getVersionNumber() + ".0"
                            : version.track.getVersionNumber()
                          : ""}
                      </CustomVersionsCountContainer>
                    </CollAndVersionContainer>
                  )}
                </TrackLeftSideContainer>
                {!isSharedContentPage && (
                  <IconButton
                    disableRipple
                    onClick={() => handleOpenDeleteVersionModal(version.track!)}
                  >
                    <DeleteOutlinedIcon />
                  </IconButton>
                )}
                {!isSharedContentPage && (
                  <CustomActionsContainer
                    sx={{ width: isSmallScreen ? "100%" : "160px" }}
                  >
                    <Button
                      onClick={() => {
                        if (version.track) {
                          makePrimaryVersion(version?.track);
                        }
                      }}
                      disabled={version.track?.getIsPrimary()}
                      sx={styles.makePrimaryButton}
                    >
                      {version.track?.getIsPrimary()
                        ? "Primary"
                        : "Make this primary"}
                    </Button>
                  </CustomActionsContainer>
                )}
              </TrackDetailsContainerForAccordion>
            </Box>
          );
        })}
      </Box>
      {!isSharedContentPage && (
        <MenuItem
          sx={shareModalStyles.addVersionButton}
          onClick={() => {
            handleOpenTrackVersionModal(track.track!, latestVersionNumber);
          }}
        >
          <img
            style={{
              margin: "2px",
              marginRight: "8px",
              filter: THEME === "light" ? "invert(100%)" : "invert(0%)",
            }}
            src="/assets/icons/TracksPageIcons/plus.svg"
            alt=""
          />
          Add Version
        </MenuItem>
      )}
    </>
  );
};
